import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import {
  getToken,
  getRefreshToken,
  setToken,
  setRefreshToken,
  isRefreshTokenExpired,
  removeToken,
  removeRefreshToken,
} from './common';

// Add custom request config property to Axios types
declare module 'axios' {
  export interface AxiosRequestConfig {
    skipAuth?: boolean;
    _retry?: boolean;
  }
}

// Global state để quản lý refresh token - SHARED giữa axios và auth.hook
let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null;
let refreshSubscribers: Array<(token: string) => void> = [];
let refreshFailureSubscribers: Array<() => void> = [];
let globalBaseURL: string = '';

// Export function để check xem đang refresh không
export function isTokenRefreshing(): boolean {
  return isRefreshing;
}

// Export function để đợi refresh xong
export async function waitForTokenRefresh(): Promise<string | null> {
  if (isRefreshing && refreshPromise) {
    return await refreshPromise;
  }
  return null;
}

// Function để thông báo tất cả subscribers khi token được refresh
function onTokenRefreshed(token: string) {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
  refreshFailureSubscribers = []; // Clear failure subscribers khi refresh thành công
}

// Function để clear data và redirect về login khi refresh thất bại
function handleRefreshFailure() {
  // Clear tất cả tokens và data
  removeToken();
  removeRefreshToken();

  // Thông báo tất cả subscribers về refresh failure
  refreshFailureSubscribers.forEach((cb) => cb());
  refreshFailureSubscribers = [];
  refreshSubscribers = [];

  // Redirect về trang login
  if (typeof window !== 'undefined') {
    window.location.href = '/login';
  }
}

// Function để refresh token - CHỈ GỌI 1 LẦN DUY NHẤT
async function refreshAccessToken(baseURL?: string): Promise<string | null> {
  const apiBaseURL = baseURL || globalBaseURL || import.meta.env.VITE_API_URL;

  // Nếu đang refresh, return promise hiện tại
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  // Bắt đầu refresh
  isRefreshing = true;

  refreshPromise = (async () => {
    try {
      const refreshToken = getRefreshToken();

      if (!refreshToken || isRefreshTokenExpired(refreshToken)) {
        throw new Error('No valid refresh token');
      }

      // Tạo một axios instance mới không có interceptor để tránh loop
      const refreshAxios = axios.create({
        baseURL: apiBaseURL,
      });

      const response = await refreshAxios.post('/v1/api/authenticate/refresh_token', {
        refreshToken: refreshToken,
      });

      const { content, succeeded } = response.data;

      if (succeeded && content?.accessToken) {
        const newAccessToken = content.accessToken;
        const newRefreshToken = content.refreshToken;

        // Lưu token mới vào localStorage
        setToken(newAccessToken);
        if (newRefreshToken) {
          setRefreshToken(newRefreshToken);
        }

        // Đảm bảo token đã được lưu vào localStorage trước khi return
        const savedToken = getToken();
        if (savedToken !== newAccessToken) {
          setToken(newAccessToken);
        }

        return newAccessToken;
      }

      throw new Error('Refresh failed');
    } catch (error) {
      console.error('❌ Token refresh error:', error);

      // Kiểm tra nếu refresh API trả về 401 hoặc 403 -> đá ra trang login ngay
      if (axios.isAxiosError(error) && error.response) {
        const status = error.response.status;
        if (status === 401 || status === 403) {
          // Refresh token không hợp lệ hoặc đã hết hạn -> redirect về login
          handleRefreshFailure();
          return null;
        }
      }

      return null;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

function getAxios(baseURL: string) {
  globalBaseURL = baseURL;
  const ins = axios.create({
    baseURL,
  });

  // 🔹 Request interceptor
  ins.interceptors.request.use(
    // 👉 đổi sang async để có thể chờ refreshPromise
    async function (config: InternalAxiosRequestConfig) {
      if (!config.headers) {
        config.headers = {} as any;
      }

      // Skip auth cho các endpoint public / auth
      if (
        config.skipAuth ||
        config.url?.includes('/refresh') ||
        config.url?.includes('/refresh_token') ||
        config.url?.includes('/login') ||
        config.url?.includes('/register')
      ) {
        (config.headers as any)['Content-Type'] = 'application/json';
        return config;
      }

      // ❗ Nếu đang refresh token: ĐỢI refresh xong rồi mới gửi request
      if (isRefreshing && refreshPromise) {
        const newToken = await refreshPromise;

        if (!newToken) {
          // Refresh thất bại → để response/error handler xử lý redirect
          throw new Error('Token refresh failed');
        }

        (config.headers as any)['Authorization'] = `Bearer ${newToken}`;
        (config.headers as any)['Content-Type'] = 'application/json';
        return config;
      }

      // Nếu đã có Authorization header (ví dụ request đã được gắn token khi retry), không ghi đè
      if ((config.headers as any)['Authorization']) {
        (config.headers as any)['Content-Type'] = 'application/json';
        return config;
      }

      // Bình thường: đọc token hiện tại và set vào header
      const accessToken = getToken();
      if (accessToken) {
        (config.headers as any)['Authorization'] = `Bearer ${accessToken}`;
      }

      (config.headers as any)['Content-Type'] = 'application/json';
      return config;
    },
    function (error) {
      return Promise.reject(error);
    },
  );

  // 🔹 Response interceptor for handling token refresh
  ins.interceptors.response.use(
    function (response: AxiosResponse) {
      return response;
    },
    async function (error: AxiosError) {
      const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

      // Skip refresh logic cho các endpoint auth / public
      if (
        originalRequest?.skipAuth ||
        originalRequest?.url?.includes('/refresh') ||
        originalRequest?.url?.includes('/refresh_token') ||
        originalRequest?.url?.includes('/login') ||
        originalRequest?.url?.includes('/register')
      ) {
        return Promise.reject(error);
      }

      // Nếu lỗi không phải 401/403 hoặc đã retry rồi → quăng ra luôn
      if (
        !error.response ||
        ![401, 403].includes(error.response.status!) ||
        originalRequest?._retry
      ) {
        return Promise.reject(error);
      }

      // Nếu đang refresh, đợi refresh xong rồi retry
      if (isRefreshing && refreshPromise) {
        return new Promise((resolve, reject) => {
          refreshPromise!
            .then((newToken) => {
              if (newToken) {
                const latestToken = getToken();
                const tokenToUse = latestToken || newToken;

                onTokenRefreshed(tokenToUse);

                if (originalRequest.headers) {
                  originalRequest.headers['Authorization'] = `Bearer ${tokenToUse}`;
                }

                const retryConfig = {
                  ...originalRequest,
                  headers: {
                    ...originalRequest.headers,
                    Authorization: `Bearer ${tokenToUse}`,
                  },
                };

                ins
                  .request(retryConfig)
                  .then((response) => resolve(response))
                  .catch((err) => reject(err));
              } else {
                handleRefreshFailure();
                reject(new Error('Token refresh failed'));
              }
            })
            .catch((err) => {
              handleRefreshFailure();
              reject(err);
            });
        });
      }

      // Chỉ có request 401 đầu tiên mới chạy tới đây
      originalRequest._retry = true;

      try {
        const newToken = await refreshAccessToken(baseURL);

        if (newToken) {
          const latestToken = getToken();
          const tokenToUse = latestToken || newToken;

          onTokenRefreshed(tokenToUse);

          if (originalRequest.headers) {
            originalRequest.headers['Authorization'] = `Bearer ${tokenToUse}`;
          }

          const retryConfig = {
            ...originalRequest,
            headers: {
              ...originalRequest.headers,
              Authorization: `Bearer ${tokenToUse}`,
            },
          };

          return ins.request(retryConfig);
        } else {
          handleRefreshFailure();
          return Promise.reject(error);
        }
      } catch (refreshError) {
        handleRefreshFailure();
        return Promise.reject(refreshError);
      }
    },
  );

  return ins;
}

// Ensure VITE_API_URL is defined
if (!import.meta.env.VITE_API_URL) {
  throw new Error('VITE_API_URL environment variable is not defined');
}

// Create an axios instance with the base URL
export const API = getAxios(`${import.meta.env.VITE_API_URL}`);

// Export refreshAccessToken để sử dụng ở nơi khác (như auth.hook.ts)
export { refreshAccessToken };
